// components/filter/FilterDrawer.tsx

import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Form,
    Input,
    Button,
    Select,
    Space,
    Divider,
    Typography,
    DatePicker, Tag
} from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { ConfigurationFilter, ConfigurationStatus } from '../../types/configuration';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface FilterDrawerProps {
    open: boolean;
    onClose: () => void;
    currentFilter: ConfigurationFilter;
    onApply: (filter: ConfigurationFilter) => void;
    onReset: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
                                                       open,
                                                       onClose,
                                                       currentFilter,
                                                       onApply,
                                                       onReset
                                                   }) => {
    const [form] = Form.useForm();
    const [localFilter, setLocalFilter] = useState<ConfigurationFilter>(currentFilter);

    // Update form when the currentFilter changes
    useEffect(() => {
        form.setFieldsValue(currentFilter);
        setLocalFilter(currentFilter);
    }, [form, currentFilter]);

    // Handle form submission
    const handleSubmit = () => {
        form.validateFields().then(values => {
            onApply(values);
        });
    };

    // Handle form reset
    const handleReset = () => {
        form.resetFields();
        onReset();
    };

    return (
        <Drawer
            title={
                <div className="flex items-center">
                    <FilterOutlined className="mr-2" />
                    <span>Filter Configurations</span>
                </div>
            }
            placement="right"
            closable={true}
            onClose={onClose}
            open={open}
            width={380}
            footer={
                <div className="flex justify-between">
                    <Button onClick={handleReset} icon={<ClearOutlined />}>
                        Reset
                    </Button>
                    <Button type="primary" onClick={handleSubmit}>
                        Apply Filters
                    </Button>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={currentFilter}
                onValuesChange={(_, allValues) => {
                    setLocalFilter(allValues);
                }}
            >
                <Form.Item name="name" label="Name">
                    <Input placeholder="Filter by name" allowClear />
                </Form.Item>

                <Form.Item name="version" label="Version">
                    <Input placeholder="Filter by version" allowClear />
                </Form.Item>

                <Form.Item name="description" label="Description">
                    <Input placeholder="Filter by description" allowClear />
                </Form.Item>

                <Form.Item name="status" label="Status">
                    <Select placeholder="Select status" allowClear>
                        <Option value={ConfigurationStatus.DRAFT}>Draft</Option>
                        <Option value={ConfigurationStatus.ACTIVE}>Active</Option>
                        <Option value={ConfigurationStatus.ARCHIVED}>Archived</Option>
                    </Select>
                </Form.Item>

                <Divider />

                <Form.Item name="dateRange" label="Created Date Range">
                    <RangePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="creator" label="Created By">
                    <Input placeholder="Filter by creator" allowClear />
                </Form.Item>

                <Divider />

                <div className="mb-4">
                    <Title level={5}>Advanced Filters</Title>
                    <Text type="secondary">
                        Additional filter options for complex queries
                    </Text>
                </div>

                <Form.Item name="hasSubscriptions" label="Subscription Status">
                    <Select placeholder="Select subscription status" allowClear>
                        <Option value="true">Has Active Subscriptions</Option>
                        <Option value="false">No Active Subscriptions</Option>
                    </Select>
                </Form.Item>

                <Form.Item name="nodeTypes" label="Contains Node Types">
                    <Select
                        mode="multiple"
                        placeholder="Select node types"
                        allowClear
                    >
                        <Option value="maker">Initiator</Option>
                        <Option value="validator">Validator</Option>
                        <Option value="router">Router</Option>
                        <Option value="executor">Executor</Option>
                        <Option value="timer">Timer</Option>
                        <Option value="alerter">Alerter</Option>
                    </Select>
                </Form.Item>
            </Form>

            {Object.keys(localFilter).length > 0 && (
                <div className="mt-4">
                    <Title level={5}>Active Filters</Title>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(localFilter).map(([key, value]) => {
                            if (!value || (Array.isArray(value) && value.length === 0)) {
                                return null;
                            }

                            return (
                                <Tag
                                    key={key}
                                    closable
                                    onClose={() => {
                                        const newFilter = { ...localFilter };
                                        // @ts-ignore
                                        delete newFilter[key];
                                        form.setFieldsValue({ [key]: undefined });
                                        setLocalFilter(newFilter);
                                    }}
                                >
                                    {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                                </Tag>
                            );
                        })}
                    </div>
                </div>
            )}
        </Drawer>
    );
};

export default FilterDrawer;